import { useEffect, useState } from "react";
import posthog from "@/lib/posthog";
import { BACKEND_URL } from "@/App";
import { ShadowMark } from "../../site/ShadowMark";
import "./WaitlistLanding.css";

export default function WaitlistLanding() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const prev = document.title;
    document.title = "Shadow · the to-do list does itself";
    return () => { document.title = prev; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus("error");
      setMessage("Enter a valid email.");
      return;
    }

    setStatus("submitting");
    setMessage("Adding you to the list…");

    try {
      posthog.capture("waitlist_signup", { email: trimmed, source: "landing_v2" });
    } catch (_) { /* analytics must never block submit */ }

    try {
      const res = await fetch(`${BACKEND_URL}/api/platform/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!res.ok) throw new Error("http_" + res.status);
      setStatus("success");
      setMessage("✓ You're on the list. We'll be in touch at launch.");
    } catch (err) {
      setStatus("error");
      setMessage("Couldn't reach the server. Try again in a moment.");
    }
  };

  const msgState =
    status === "success" ? "success" : status === "error" ? "error" : undefined;

  return (
    <div className="wl-root">
      {/* HERO */}
      <header className="hero">
        {/* Decorative concentric sonar rings */}
        <svg className="hero__rings" viewBox="0 0 1000 1000" aria-hidden="true" focusable="false">
          <g fill="none" stroke="#fff" strokeWidth="1.5">
            <circle className="r1" cx="500" cy="500" r="200" opacity="0" />
            <circle className="r2" cx="500" cy="500" r="200" opacity="0" />
            <circle className="r3" cx="500" cy="500" r="200" opacity="0" />
            <circle className="r4" cx="500" cy="500" r="200" opacity="0" />
            <circle cx="500" cy="500" r="120" opacity="0.10" />
            <circle cx="500" cy="500" r="240" opacity="0.07" />
            <circle cx="500" cy="500" r="360" opacity="0.05" />
          </g>
        </svg>

        <div className="wrap">
          <nav className="hero__nav">
            <span className="logo">
              <ShadowMark size={24} color="#fff" secondary="rgba(255,255,255,0.5)" /> Shadow
            </span>
            <span className="ph-pill" aria-label="Launching on ProductHunt soon">
              <span className="dot" aria-hidden="true"></span> Launching on ProductHunt soon
            </span>
          </nav>

          <div className="hero__grid">
            <div className="hero__copy hero__copy--top">
              <h1>
                Shadow completes your <span className="nb">To-Dos</span><br />
                <em>before you finish the call.</em>
              </h1>
            </div>

            <div className="hero__copy hero__copy--sub">
              <p className="hero__sub">
                PDFs sent. Slides built. Data pulled. All while you stay in the conversation.
              </p>
            </div>

            {/* LIVE-CALL MOCK */}
            <div className="mock" aria-hidden="true">
              <div className="mock__shell">
                <div className="mock__chrome">
                  <span className="dots"><span></span><span></span><span></span></span>
                  <span>Sales call · Acme Corp</span>
                  <span className="live">LIVE</span>
                </div>

                <div className="mock__stage">
                  {/* decorative pulse from where Shadow lands */}
                  <svg className="mock__pulse" viewBox="0 0 200 200" aria-hidden="true">
                    <circle cx="100" cy="100" r="40" />
                    <circle cx="100" cy="100" r="70" opacity="0.6" />
                    <circle cx="100" cy="100" r="100" opacity="0.3" />
                  </svg>

                  <div className="tile tile--main">
                    <div className="tile__avatar">S</div>
                    <span className="tile__name">Sarah · Acme</span>
                    <span className="tile__speak"></span>
                  </div>

                  <div className="tile tile--pip">
                    <div className="tile__avatar">P</div>
                    <span className="tile__name">You</span>
                  </div>

                  {/* Captions: one per scene; staggered so only one is visible at a time */}
                  <div className="caption scene-1">
                    <span className="caption__who">Sarah</span>
                    <span className="caption__text">"Could you send me the pricing PDF after this?"</span>
                  </div>
                  <div className="caption scene-2">
                    <span className="caption__who">Sarah</span>
                    <span className="caption__text">"Can you share a slide on the Q3 expansion plan?"</span>
                  </div>
                  <div className="caption scene-3">
                    <span className="caption__who">Sarah</span>
                    <span className="caption__text">"How many active users do you have right now?"</span>
                  </div>

                  {/* Shadow cards: one per scene; drop in from the top in sequence */}
                  <div className="shadow-card scene-1">
                    <div className="shadow-card__head">
                      <span className="shadow-orb" aria-hidden="true"></span>
                      <span className="shadow-card__title">Shadow <small>real-time</small></span>
                    </div>
                    <div className="shadow-card__row"><span>Action item detected</span></div>
                    <span className="shadow-card__chip">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      </svg>
                      Send pricing PDF → sarah@acme.com
                    </span>
                    <div className="shadow-card__action">
                      <span className="check">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span>Already sent.</span>
                    </div>
                    <div className="shadow-card__source">via Google Drive</div>
                  </div>

                  <div className="shadow-card scene-2">
                    <div className="shadow-card__head">
                      <span className="shadow-orb" aria-hidden="true"></span>
                      <span className="shadow-card__title">Shadow <small>real-time</small></span>
                    </div>
                    <div className="shadow-card__row"><span>Action item detected</span></div>
                    <span className="shadow-card__chip">
                      <svg viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="4" width="18" height="13" rx="1.5" stroke="currentColor" strokeWidth="2" />
                        <path d="M7 9h10M7 12h7M9 21h6M12 17v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      Build slide · Q3 expansion plan
                    </span>
                    <div className="shadow-card__action">
                      <span className="check">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span>Dropped in chat.</span>
                    </div>
                    <div className="shadow-card__source">via Google Slides</div>
                  </div>

                  <div className="shadow-card scene-3">
                    <div className="shadow-card__head">
                      <span className="shadow-orb" aria-hidden="true"></span>
                      <span className="shadow-card__title">Shadow <small>real-time</small></span>
                    </div>
                    <div className="shadow-card__row"><span>Action item detected</span></div>
                    <span className="shadow-card__chip">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 14l3-3 4 2 5-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Pull MAU from Metabase
                    </span>
                    <div className="shadow-card__action">
                      <span className="check">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span>12,847 active users.</span>
                    </div>
                    <div className="shadow-card__source">via Metabase · link in chat</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="hero__copy hero__copy--bottom">
              <form className="form" onSubmit={handleSubmit} noValidate>
                <div className="form__row">
                  <input
                    type="email"
                    name="email"
                    placeholder="you@work.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    aria-label="Work email"
                    disabled={status === "success"}
                  />
                  <button
                    type="submit"
                    disabled={status === "submitting" || status === "success"}
                  >
                    {status === "success" ? "On the list" : "Join the Waitlist"}
                  </button>
                </div>
                <div className="form__msg" role="status" aria-live="polite" data-state={msgState}>
                  {message}
                </div>
              </form>
            </div>
          </div>
        </div>
      </header>

      <footer>
        <div className="wrap">© 2026 Converse AI Inc. · shadowlabs.ai</div>
      </footer>
    </div>
  );
}
